// Formatting only -- no Flip arithmetic lives here. See README.md ("no formula on the frontend").

// Every dollar figure reads "$191,000" -- row values and section-header totals alike (Cong, letter
// 111, Comment Set #3 item 6). A negative one reads "-$26,500": the minus leads the sign.
function withDollarSign(body) {
  return body.startsWith('-') ? `-$${body.slice(1)}` : `$${body}`
}

export function formatMoney(n) {
  if (n == null || !Number.isFinite(n)) return null
  const rounded = Math.round(n)
  // Math.round(-0.4) is -0, and (-0).toLocaleString() is the string "-0" -- clamp before
  // formatting so a value that rounds to zero never displays a signed zero.
  return withDollarSign((rounded === 0 ? 0 : rounded).toLocaleString('en-US'))
}

// Which label a net profit figure carries -- "Net Loss" (and #D85263) or "Net Profit". Strictly
// negative after the same Math.round the displayed figure gets, so -0.4 (shown as "0") stays Net
// Profit and undefined (no figure yet) is never a loss. Shared by the Metrics strip and Flip
// Summary's Net Profit row so the two cannot disagree -- README.md ("Net Loss is strictly negative").
export function isNetLoss(n) {
  return Number.isFinite(n) && Math.round(n) < 0
}

// Deal Metrics strip dollars (letter 99, 6.3.1): $47,900 -> $48K. Returns the parts the strip draws
// at two sizes -- sign and "$" (28px), digits (36px), "K"/"M" (28px). Rules in README.md ("Deal Metrics
// rounding"): whole dollars under 1,000, nearest thousand with K, one-decimal M from 1,000,000 (a
// value that rounds to 1,000K is shown as $1M). The strip only -- full figures everywhere else.
export function formatCompactMoneyParts(n) {
  if (n == null || !Number.isFinite(n)) return null
  const abs = Math.abs(n)
  const whole = Math.round(abs)
  const sign = n < 0 && whole !== 0 ? '-' : ''
  if (whole < 1000) return { sign, body: String(whole), suffix: null }
  const thousands = Math.round(abs / 1000)
  if (thousands < 1000) return { sign, body: String(thousands), suffix: 'K' }
  const millions = Math.round(abs / 100000) / 10
  return { sign, body: Number.isInteger(millions) ? String(millions) : millions.toFixed(1), suffix: 'M' }
}

// Canvas note verbatim: "% formats are x.xx%, except for 0%, which displays without the zeros
// behind the decimals." Read-only derived percentages only -- editable percent inputs show the raw
// typed number plus a static "%" suffix instead (unspecified whether to reformat mid-typing).
// `decimals`: letter 99 overrides the two for the Deal Metrics strip (0, item 6.3.2) and LTC / LTARV
// (1, item 6.4); 0% keeps its no-decimals exception at every precision.
export function formatPercent(n, decimals = 2) {
  if (n == null || !Number.isFinite(n)) return null
  const body = n.toFixed(decimals)
  // toFixed leaves the sign character in the string -- n in (-0.005, 0) rounds to "-0.00", not
  // caught by a literal n === 0 check. Number(body) folds "-0.00" and "0.00" to the same 0.
  if (Number(body) === 0) return '0%'
  return `${body}%`
}

// The dash every other empty/derived field on this codebase already uses -- brand.placeholder in
// tailwind.config.js, the same token AddOpportunity's inputs and the Notes placeholder carry.
export const PLACEHOLDER = '—'

// Port of reilm-be/modules/properties/flip/refinanceSummary.js's formatCashPosition -- the backend
// computes computeFlipScenario's br4_refi_profit signed and never calls its own helper, so the
// display-layer split happens here. Canvas text's own boundary is ">= 0" ("When (Cash-Out Proceeds -
// Total Cash Invested) >= 0, display 'Excess Cash Out'"), not legacy's own "> 0" -- keep this in sync
// with the backend function if that boundary ever changes; see README.md ("Refinance Summary").
export function formatCashPosition(netCashPosition) {
  if (!Number.isFinite(netCashPosition)) return { label: 'Excess Cash Out', value: 0 }
  return netCashPosition >= 0
    ? { label: 'Excess Cash Out', value: netCashPosition }
    : { label: 'Cash Left in Deal', value: Math.abs(netCashPosition) }
}

// Formats a backend-computed schedule date as spec 4.2.1 draws it: M/D/YY, no leading zeros
// ("7/3/24") -- re-rendering, not computing. UTC getters: local ones read a UTC-midnight instant
// one calendar day early in any timezone west of Greenwich.
export function formatScheduleDate(d) {
  if (d == null) return null
  const date = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(date.getTime())) return null
  const yy = String(date.getUTCFullYear()).slice(-2)
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${yy}`
}

// A payment-schedule row as the design draws it (`2,354.63`, `560.00`): two decimals, cents kept.
export function formatSchedulePayment(n) {
  if (n == null || !Number.isFinite(n)) return null
  const cents = Math.round(n * 100) / 100
  return (cents === 0 ? 0 : cents).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// A payment-schedule figure as a dollar amount, cents kept: "$2,354.63" (letter 111 item 6).
export function formatMoneyCents(n) {
  const body = formatSchedulePayment(n)
  return body == null ? null : withDollarSign(body)
}

// An editable money box's stored value as shown while the box is not being edited (letter 111 item 6):
// "285000" -> "$285,000", "1234.5" -> "$1,234.50" -- whole dollars, or exactly two decimals when the
// figure has a fraction; `cents` always two (the payment boxes). Text that is not a number is left as is.
export function formatMoneyInput(value, { cents = false } = {}) {
  const text = value == null ? '' : String(value).trim()
  if (text === '') return ''
  const n = Number(text)
  if (!Number.isFinite(n)) return text
  if (cents || !Number.isInteger(n)) return formatMoneyCents(n)
  return withDollarSign((n === 0 ? 0 : n).toLocaleString('en-US'))
}

// Timeline months as the design draws them (`4.0`): a whole number keeps one decimal, anything else
// is shown as stored. Blank stays blank.
export function formatTimelineMonths(n) {
  if (typeof n === 'string') return n
  if (n == null || !Number.isFinite(n)) return ''
  return Number.isInteger(n) ? n.toFixed(1) : String(n)
}

// Header "Last Modified" (holding-costs-header spec 4.1): "Oct 25, 2026 10:14 AM" -- no comma after
// the year, which toLocaleString always adds, so the parts are joined by hand. Local time.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatModified(value) {
  if (!(typeof value === 'string' && value.trim()) && !(value instanceof Date)) return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const hour12 = hours % 12 === 0 ? 12 : hours % 12
  const meridiem = hours < 12 ? 'AM' : 'PM'
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} ${hour12}:${minutes} ${meridiem}`
}
